import { z } from 'zod';

// Importar schemas de dependências
import { AddressSchema } from '../../../models/address';
import { FeedbackEntrySchema } from './feedback';
import { BankAccountSchema } from '../../../models/bankAccount';

export { FeedbackEntrySchema as FeedbackEntry } from './feedback';
export type { FeedbackType } from './feedback';


// Tipo para metadados contextuais
export interface FieldMetadata {
    contexts: string[];
    required: string[];
    fieldset: string;
}

// Helper para adicionar metadados aos campos Zod
const withMetadata = <T extends z.ZodTypeAny>(schema: T, metadata: FieldMetadata) => {
    return schema.describe(JSON.stringify(metadata));
};

export const ChildSchema = z.object({
    name: z.string().optional(),
    sex: z.enum(['Masculino', 'Feminino', 'Outro']),
    birthDate: z.string(),
    birthCertificateUrl: z.string().optional(),
    identityDocumentUrl: z.string().optional(),
});

export const HealthDependentSchema = z.object({
    name: z.string(),
    relationship: z.enum(['Cônjuge', 'Filho(a)']),
    isCovered: z.boolean(),
    cardNumber: z.string().optional(),
});

export const EmergencyContactSchema = z.object({
    name: z.string().optional(),
    phone: z.string().optional(),
    relationship: z.string().optional(),
});

export const VacationPeriodSchema = z.object({
    id: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    days: z.number().optional(),
    observations: z.string().optional(),
    hasSellDays: z.boolean().optional(),
});

export const PaidRestPeriodSchema = z.object({
    id: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    days: z.number().optional(),
    observations: z.string().optional(),
});

/** Situação da formação — mesmas opções do formulário do candidato (Etapa 2). */
export const EDUCATION_STATUS_OPTIONS = ['Concluído', 'Em andamento', 'Trancado'] as const;

// Para o RH importam tipo, curso e situação; instituição e datas são opcionais.
export const EducationEntrySchema = z.object({
    id: z.string().optional(),
    type: z.enum(['Ensino Médio', 'Graduação', 'Pós-graduação', 'Mestrado', 'Doutorado']),
    course: z.string().optional(),
    status: z.string().optional(),
    institution: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    diplomaUrl: z.string().optional(),
});



export const MedicalCertificateSchema = z.object({
    id: z.string().optional(),
    date: z.string(),
    cid: z.string(),
    days: z.number(),
    certificateUrl: z.string().optional(),
    certificateFile: z.any().optional(), // ✅ Validação específica feita no Controller
});

export const PeriodicExamSchema = z.object({
    id: z.string().optional(),
    examDate: z.string(),
    examType: z.enum(['Periódico', 'Mudança de Função', 'Retorno ao Trabalho']),
    nextExamDate: z.string(),
    doctorName: z.string().optional(),
    clinicName: z.string().optional(),
    result: z.enum(['Apto', 'Inapto', 'Apto com restrições']),
    restrictions: z.string().optional(),
    examUrl: z.string().optional(),
    examFile: z.any().optional(), // ✅ Validação específica feita no Controller
});

export const IdentityDocumentSchema = z.object({
    number: z.string().optional(),
    issuer: z.string().optional(),
    uf: z.string().optional(),
    issueDate: z.string().nullable().optional(),
    idDocumentFrontUrl: z.string().optional(),
    idDocumentBackUrl: z.string().optional(),
});

export const ResourceSchema = z.object({
    typeId: z.string(),
    technologyId: z.string(),
    seniorityId: z.string(),
    isPrimary: z.boolean(),
});

export const RoleTypeSchema = z.enum(['operational', 'administrative']);
export const ShirtSizeSchema = z.enum(['P', 'M', 'G', 'GG', 'GGG', 'GGGG', 'Outro']);
export const CollaboratorTypeSchema = z.enum(['Celetista', 'Fornecedor', 'Sócio', 'Estagiário']);
export const StatusTypeSchema = z.enum(['PENDING', 'UPDATED', 'IN_PROGRESS']);
export const WorkModelPreferenceSchema = z.enum(['Remote', 'Hybrid', 'Onsite']);

export const UpdateStatusEntrySchema = z.object({
    byUser: z.object({
        status: StatusTypeSchema,
        currentStep: z.number().optional(),
        lastUpdateWindowId: z.string().nullish(), // ID da última janela em que atualizou (aceita null e undefined)
        lastUpdateDate: z.string().nullish(), // Data da última atualização (aceita null e undefined)
    }).optional(),
    byDHO: z.object({ status: StatusTypeSchema }).optional(),
    byDP: z.object({ status: StatusTypeSchema }).optional(),
    byTech: z.object({ status: StatusTypeSchema }).optional(),
});

export const PcdTypeSchema = z.enum(['Física', 'Auditiva', 'Visual', 'Mental', 'Intelectual']);

export const SalaryEntrySchema = z.object({
    id: z.string().optional(),
    date: z.string(),
    amount: z.number(),
    extras: z.number().optional(),
    salaryType: z.string(),
    reason: z.string(),
    observations: z.string().optional(),
});

export const BaseCollaboratorSchema = z.object({
    id: z.string().optional(),
    name: z.string(),
    status: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),
    updatedStatus: UpdateStatusEntrySchema.nullable().optional(),

    // Campo contextual específico do CapabilitiesFieldSet
    resources: withMetadata(
        z.array(ResourceSchema).nullable().optional()
            .refine((resources) => {
                // Validação 1: pelo menos uma capacidade principal
                if (!resources || resources.length === 0) return true;
                return resources.some(resource => resource.isPrimary);
            }, 'Deve ter pelo menos uma capacidade principal')
            .refine((resources) => {
                // Validação 2: todas as capacidades devem estar completas
                if (!resources || resources.length === 0) return true;
                return resources.every(resource =>
                    resource.typeId &&
                    resource.technologyId &&
                    resource.seniorityId
                );
            }, 'Todas as capacidades devem ter tipo, tecnologia e senioridade preenchidos'),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'CapabilitiesFieldSet'
        }
    ),
    photoUrl: z.string().nullable().optional(),
    // Campos contextuais específicos do HiringFieldSet
    positionId: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),

    positionName: z.string().nullable().optional(),

    seniorityId: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),

    seniorityName: z.string().nullable().optional(),
    department: z.string().nullable().optional(),

    roleType: withMetadata(
        z.enum(['administrative', 'operational']).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),

    startDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),

    endDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),

    terminationReason: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),

    terminationAllocationId: z.string().nullable().optional(),

    experienceEndDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),
    responsibleId: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE'],
            required: ['DHO_UPDATE'],
            fieldset: 'ResponsibilitiesFieldSet'
        }
    ),

    responsibleName: z.string().nullable().optional(),

    // Campo com metadados contextuais - AddressFieldSet
    isMailingSameAsMain: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'AddressFieldSet'
        }
    ),


    identityDocument: withMetadata(
        IdentityDocumentSchema.nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),
    collaboratorType: withMetadata(
        CollaboratorTypeSchema.nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),
    addresses: withMetadata(
        z.array(AddressSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'AddressFieldSet'
        }
    ),
    email: z.string().nullable().optional(),

    // Campos contextuais específicos do PersonalDataFieldSet
    personalEmail: withMetadata(
        z.string().nullable().optional()
            .refine((value) => {
                // Validação de formato de email será implementada em runtime
                if (!value || value.trim() === '') return true;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(value);
            }, 'E-mail deve ter um formato válido'),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    phone: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    // Campo contextual específico do IdentificationFieldSet
    cpf: withMetadata(
        z.string().nullable().optional()
            .refine((value) => {
                // Validação de CPF será implementada em runtime
                if (!value || value.trim() === '') return true;
                // Placeholder para validação de CPF - implementar isValidCpf
                return true;
            }, 'CPF inválido'),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    birthDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    nationality: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    naturality: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    birthState: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),
    // Mais campos contextuais do IdentificationFieldSet
    esocialCode: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    // Campos contextuais específicos do SignedContractsFieldSet
    workContractFile: withMetadata(
        z.any().optional(), // ✅ Validação específica feita no Controller ou via refine
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [], // ✅ Validação será feita via refine (arquivo OU URL)
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    ndaFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    equipmentUsageFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    imageRightsFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    workContractUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    ndaUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    equipmentUsageUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    imageRightsUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'SignedContractsFieldSet'
        }
    ),

    // === Documentos Familiares ===
    marriageCertificateFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    marriageCertificateUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    spouseIdentityFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    spouseIdentityUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    motherIdentityFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    motherIdentityUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    fatherIdentityFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    fatherIdentityUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'FamilyDocumentsFieldSet'
        }
    ),

    // Campos contextuais específicos do FiliationAndMaritalStatusFieldSet
    motherName: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'FiliationAndMaritalStatusFieldSet'
        }
    ),

    fatherName: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'FiliationAndMaritalStatusFieldSet'
        }
    ),

    // `.catch(undefined)`: dado legado importado da automação antiga do SharePoint/Forms
    // (que aceitava texto livre) às vezes não bate exatamente com nenhuma destas opções
    // (ex. "Casado (a)" com espaço) — sem isso, um valor inválido travava a validação do
    // formulário INTEIRO no submit, mesmo editando um campo sem nenhuma relação (ver
    // conversa registrada). Cai pra "não informado" em vez de bloquear o salvamento.
    maritalStatus: withMetadata(
        z
            .enum(['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'])
            .nullable()
            .optional()
            .catch(undefined),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'FiliationAndMaritalStatusFieldSet'
        }
    ),

    spouseName: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [], // Condicional, não sempre obrigatório - validação será implementada no nível do formulário
            fieldset: 'FiliationAndMaritalStatusFieldSet'
        }
    ),
    // Campo contextual específico do ChildrenFieldSet
    children: withMetadata(
        z.array(ChildSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'ChildrenFieldSet'
        }
    ),

    // Mais campos contextuais do PersonalDataFieldSet
    sex: withMetadata(
        z.enum(['Masculino', 'Feminino', 'Outro']).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    gender: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    // `.catch(undefined)`: mesmo motivo de `maritalStatus` acima — dado legado do
    // SharePoint/Forms podia não bater com nenhuma opção exata.
    race: withMetadata(
        z
            .enum(['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Outra'])
            .nullable()
            .optional()
            .catch(undefined),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'PersonalDataFieldSet'
        }
    ),

    // Campos contextuais específicos do HealthPlanFieldSet
    hasHealthInsurance: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'HealthPlanFieldSet'
        }
    ),

    healthInsuranceProvider: withMetadata(
        z.enum(['Bradesco', 'Unimed', 'Noterdame', 'Outro']).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [], // Condicional baseado em hasHealthInsurance - validação no nível do formulário
            fieldset: 'HealthPlanFieldSet'
        }
    ),

    healthInsuranceInclusionDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [], // Condicional baseado em hasHealthInsurance - validação no nível do formulário
            fieldset: 'HealthPlanFieldSet'
        }
    ),

    healthInsuranceCardNumber: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [], // Condicional baseado em hasHealthInsurance - validação no nível do formulário
            fieldset: 'HealthPlanFieldSet'
        }
    ),

    healthDependents: withMetadata(
        z.array(HealthDependentSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'HealthPlanFieldSet'
        }
    ),
    healthPlanIdentifiers: withMetadata(
        z.record(z.string(), z.string()).nullable().optional(),
        {
            contexts: [],
            required: [],
            fieldset: 'HealthPlanFieldSet'
        }
    ),
    // Campo contextual específico do EmergencyContactFieldSet
    emergencyContact: withMetadata(
        EmergencyContactSchema.nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'], // Obrigatório apenas no PROFILE_UPDATE
            fieldset: 'EmergencyContactFieldSet'
        }
    ),
    // Campos contextuais específicos do SpecialNeedsFieldSet
    isPCD: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'SpecialNeedsFieldSet'
        }
    ),

    pcdTypes: withMetadata(
        z.array(PcdTypeSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: [], // Condicional baseado em isPCD - validação no nível do formulário
            fieldset: 'SpecialNeedsFieldSet'
        }
    ),

    pcdDocumentFile: withMetadata(
        z.any().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'SpecialNeedsFieldSet'
        }
    ),

    pcdDocumentUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'SpecialNeedsFieldSet'
        }
    ),

    // Campos contextuais específicos do MedicalExamsFieldSet
    admissionExamUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'MedicalExamsFieldSet'
        }
    ),

    terminationExamUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'MedicalExamsFieldSet'
        }
    ),

    admissionExamFile: withMetadata(
        z.any().optional(), // ✅ Validação específica feita no Controller
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'MedicalExamsFieldSet'
        }
    ),

    terminationExamFile: withMetadata(
        z.any().optional(), // ✅ Validação específica feita no Controller
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'MedicalExamsFieldSet'
        }
    ),
    // Campo contextual específico do MedicalCertificatesFieldSet
    medicalCertificates: withMetadata(
        z.array(MedicalCertificateSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'MedicalCertificatesFieldSet'
        }
    ),

    // Campo contextual específico do PeriodicExamsFieldSet
    periodicExams: withMetadata(
        z.array(PeriodicExamSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE'],
            required: [],
            fieldset: 'PeriodicExamsFieldSet'
        }
    ),

    // Campos contextuais específicos do GiftsAndPreferencesFieldSet
    // `.catch(undefined)`: mesmo motivo de `maritalStatus` acima (ex. "EXG", que nunca foi
    // opção válida de tamanho, importado do Forms antigo).
    shirtSize: withMetadata(
        ShirtSizeSchema.nullable().optional().catch(undefined),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    slipperSize: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    hasOnboardingKit: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    hasBackpack: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    freeTimeActivities: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    favoriteFoodAndDrink: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    favoriteColor: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    favoriteBook: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    favoriteMovieOrSeries: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    favoriteCharacter: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    sportsPractice: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    specialTalent: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    futureGoals: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    generalObservations: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'GiftsAndPreferencesFieldSet'
        }
    ),

    // Campos contextuais específicos do BenefitsFieldSet
    hasCajuBenefit: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    hasWellhubBenefit: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    hasZeloBenefit: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    zeloDependents: withMetadata(
        z.array(z.string()).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    hasGuapecoBenefit: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    transportationBenefitAmount: withMetadata(
        z.number().nullable().optional().refine((value) => {
            // Validação: valor deve ser positivo se fornecido
            if (value !== null && value !== undefined) {
                return value >= 0;
            }
            return true;
        }, 'Valor deve ser positivo'),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'BenefitsFieldSet'
        }
    ),

    // Campos contextuais específicos do VacationFieldSet
    showVacationPeriods: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'VacationFieldSet'
        }
    ),

    vacationPeriods: withMetadata(
        z.array(VacationPeriodSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'VacationFieldSet'
        }
    ),

    hasPaidRest: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'VacationFieldSet'
        }
    ),

    paidRestObservations: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'VacationFieldSet'
        }
    ),

    paidRestPeriods: withMetadata(
        z.array(PaidRestPeriodSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'VacationFieldSet'
        }
    ),

    // Mais campos contextuais específicos do HiringFieldSet
    workSchedule: withMetadata(
        z.number().nullable().optional().refine((value) => {
            // Validação: carga horária deve estar entre 1 e 24 horas se fornecida
            if (value !== null && value !== undefined) {
                return value >= 1 && value <= 24;
            }
            return true;
        }, 'Carga horária deve ser entre 1 e 24 horas'),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: ['DP_UPDATE'],
            fieldset: 'HiringFieldSet'
        }
    ),
    preferredWorkModels: withMetadata(
        z.array(WorkModelPreferenceSchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),

    isRaroAcademyAlumnus: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'HiringFieldSet'
        }
    ),

    cnpj: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Fornecedor' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),

    internshipEducationalInstitution: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Estagiário' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),

    internshipCourse: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Estagiário' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),

    internshipEndDate: z.string().nullable().optional(),

    internshipContractUrl: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Estagiário' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),

    companyName: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Fornecedor' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),

    internshipContractFile: withMetadata(
        z.instanceof(File).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Condicional baseado em collaboratorType === 'Estagiário' - validação no nível do formulário
            fieldset: 'HiringFieldSet'
        }
    ),
    // Campos contextuais específicos do ResponsibilitiesFieldSet
    departmentId: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE'],
            required: ['DHO_UPDATE'],
            fieldset: 'ResponsibilitiesFieldSet'
        }
    ),

    clientResponsible: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE'],
            required: [],
            fieldset: 'ResponsibilitiesFieldSet'
        }
    ),

    // Performance Info - PerformanceFieldSet
    rating: withMetadata(
        z.number().min(1, 'Rating deve ser entre 1 e 10').max(10, 'Rating deve ser entre 1 e 10').default(5).optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE', 'DP_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'PerformanceFieldSet'
        }
    ),

    // Campos contextuais específicos do BankAccountFieldSet
    salaryAccount: withMetadata(
        BankAccountSchema.nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    personalAccount: withMetadata(
        BankAccountSchema.nullable().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    // Campos de paths aninhados para validação granular - BankAccountFieldSet
    'salaryAccount.bankCode': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'salaryAccount.agency': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'salaryAccount.account': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'salaryAccount.digit': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'salaryAccount.pixKey': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'personalAccount.bankCode': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'personalAccount.agency': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'personalAccount.account': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'personalAccount.digit': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    'personalAccount.pixKey': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'BankAccountFieldSet'
        }
    ),

    // Campos contextuais específicos do NewSalaryEntryFieldSet
    newSalaryEntryDate: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Não obrigatório - validação apenas no botão "Adicionar"
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    newSalaryEntryAmount: withMetadata(
        z.number().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Não obrigatório - validação apenas no botão "Adicionar"
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    newSalaryEntryExtras: withMetadata(
        z.number().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    newSalaryEntrySalaryType: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Não obrigatório - validação apenas no botão "Adicionar"
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    newSalaryEntryReason: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [], // Não obrigatório - validação apenas no botão "Adicionar"
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    newSalaryEntryObservations: withMetadata(
        z.string().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'NewSalaryEntryFieldSet'
        }
    ),

    // Campos contextuais específicos do SalaryHistoryFieldSet
    salaryHistoryDeletedIds: withMetadata(
        z.array(z.string()).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'SalaryHistoryFieldSet'
        }
    ),

    salaryHistoryShowHistory: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'SalaryHistoryFieldSet'
        }
    ),

    salaryHistoryAllowRemove: withMetadata(
        z.boolean().nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'SalaryHistoryFieldSet'
        }
    ),

    salaryHistorySalaries: withMetadata(
        z.array(SalaryEntrySchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE'],
            required: [],
            fieldset: 'SalaryHistoryFieldSet'
        }
    ),

    // Campos contextuais específicos do FeedbackListFieldSet
    occurrences: withMetadata(
        z.array(FeedbackEntrySchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'FeedbackListFieldSet'
        }
    ),

    raroFeedbacks: withMetadata(
        z.array(FeedbackEntrySchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'FeedbackListFieldSet'
        }
    ),

    clientFeedbacks: withMetadata(
        z.array(FeedbackEntrySchema).nullable().optional(),
        {
            contexts: ['FULL_FORM', 'DHO_UPDATE'],
            required: [],
            fieldset: 'FeedbackListFieldSet'
        }
    ),

    // Campos contextuais específicos do AddressFieldSet (representam validações de paths aninhados)
    'addresses.0.fullAddress': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.0.number': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.0.complement': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.0.proofOfAddressFile': withMetadata(
        z.any().optional(), // ✅ Aceita qualquer valor - validação é feita no Controller
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [], // Não obrigatório individualmente, validação "arquivo OU URL" é feita via Controller customizado
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.0.proofOfAddressUrl': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [], // Não obrigatório individualmente, validação "arquivo OU URL" é feita via customização
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.1.fullAddress': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.1.number': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'AddressFieldSet'
        }
    ),

    'addresses.1.complement': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
            required: [],
            fieldset: 'AddressFieldSet'
        }
    ),

    // Campos de arquivo específicos do IdentificationFieldSet
    idDocumentFrontFile: withMetadata(
        z.any().optional(), // ✅ Validação específica feita no Controller
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [], // ✅ Validação customizada no Controller - não usar required aqui
            fieldset: 'IdentificationFieldSet'
        }
    ),

    idDocumentBackFile: withMetadata(
        z.any().optional(), // ✅ Validação específica feita no Controller
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: [],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    // Campos nested do IdentificationFieldSet (integrados ao sistema de metadados)
    'identityDocument.number': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    'identityDocument.issuer': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    'identityDocument.uf': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    'identityDocument.issueDate': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'PROFILE_UPDATE', 'DP_UPDATE', 'DHO_UPDATE', 'TECH_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'IdentificationFieldSet'
        }
    ),

    // Campos nested do EmergencyContactFieldSet (integrados ao sistema de metadados)
    'emergencyContact.name': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'EmergencyContactFieldSet'
        }
    ),

    'emergencyContact.phone': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'EmergencyContactFieldSet'
        }
    ),

    'emergencyContact.relationship': withMetadata(
        z.string().optional(),
        {
            contexts: ['FULL_FORM', 'DP_UPDATE', 'DHO_UPDATE', 'PROFILE_UPDATE'],
            required: ['PROFILE_UPDATE'],
            fieldset: 'EmergencyContactFieldSet'
        }
    ),

    // Campo contextual específico do EducationFieldSet
    educations: withMetadata(
        z.array(EducationEntrySchema).nullable().optional(),
        {
            contexts: ['FULL_FORM'],
            required: [],
            fieldset: 'EducationFieldSet'
        }
    ),

    // ✅ Campos nested são tratados através dos schemas pai (emergencyContact, salaryAccount, etc.)
    // Metadados individuais adicionados para validação granular
});

export const CollaboratorSchema = BaseCollaboratorSchema.extend({
    healthInsuranceCardNumber: z.string().nullable().optional(),
    healthInsurancePlan: z.string().nullable().optional(),
    healthInsuranceExtraInfo: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    departmentName: z.string().nullable().optional(),
    hasNotice: z.boolean().nullable().optional(),
});

// Schema específico para formulários que transforma valores null/undefined em valores adequados
export const FormCollaboratorSchema = CollaboratorSchema.transform((data) => {
    // Helper para transformar strings null/undefined em string vazia
    const normalizeString = (value: string | null | undefined): string => value ?? '';

    // Helper para transformar booleans null/undefined em false
    const normalizeBoolean = (value: boolean | null | undefined): boolean => value ?? false;

    // Helper para transformar arrays null/undefined em array vazio
    const normalizeArray = <T>(value: T[] | null | undefined): T[] => value ?? [];

    // Helper para transformar objetos null/undefined em undefined (para campos opcionais)
    const normalizeObject = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

    return {
        ...data,
        // Strings que devem ser vazias no formulário
        personalEmail: normalizeString(data.personalEmail),
        phone: normalizeString(data.phone),
        birthDate: normalizeString(data.birthDate),
        nationality: normalizeString(data.nationality),
        naturality: normalizeString(data.naturality),
        birthState: normalizeString(data.birthState),
        cpf: normalizeString(data.cpf),
        workContractUrl: normalizeString(data.workContractUrl),
        ndaUrl: normalizeString(data.ndaUrl),
        equipmentUsageUrl: normalizeString(data.equipmentUsageUrl),
        imageRightsUrl: normalizeString(data.imageRightsUrl),
        motherName: normalizeString(data.motherName),
        fatherName: normalizeString(data.fatherName),
        spouseName: normalizeString(data.spouseName),
        healthInsuranceProvider: normalizeString(data.healthInsuranceProvider),
        healthInsuranceInclusionDate: normalizeString(data.healthInsuranceInclusionDate),
        healthInsuranceCardNumber: normalizeString(data.healthInsuranceCardNumber),
        admissionExamUrl: normalizeString(data.admissionExamUrl),
        terminationExamUrl: normalizeString(data.terminationExamUrl),
        slipperSize: normalizeString(data.slipperSize),
        freeTimeActivities: normalizeString(data.freeTimeActivities),
        favoriteFoodAndDrink: normalizeString(data.favoriteFoodAndDrink),
        favoriteColor: normalizeString(data.favoriteColor),
        favoriteBook: normalizeString(data.favoriteBook),
        favoriteMovieOrSeries: normalizeString(data.favoriteMovieOrSeries),
        favoriteCharacter: normalizeString(data.favoriteCharacter),
        sportsPractice: normalizeString(data.sportsPractice),
        specialTalent: normalizeString(data.specialTalent),
        futureGoals: normalizeString(data.futureGoals),
        generalObservations: normalizeString(data.generalObservations),
        cnpj: normalizeString(data.cnpj),
        internshipEducationalInstitution: normalizeString(data.internshipEducationalInstitution),
        internshipCourse: normalizeString(data.internshipCourse),
        internshipContractUrl: normalizeString(data.internshipContractUrl),
        companyName: normalizeString(data.companyName),
        departmentId: normalizeString(data.departmentId),
        responsibleId: normalizeString(data.responsibleId),
        clientResponsible: normalizeString(data.clientResponsible),
        newSalaryEntryDate: normalizeString(data.newSalaryEntryDate),
        newSalaryEntrySalaryType: normalizeString(data.newSalaryEntrySalaryType),
        newSalaryEntryReason: normalizeString(data.newSalaryEntryReason),
        newSalaryEntryObservations: normalizeString(data.newSalaryEntryObservations),
        paidRestObservations: normalizeString(data.paidRestObservations),

        // Booleans que devem ser false no formulário
        isMailingSameAsMain: normalizeBoolean(data.isMailingSameAsMain),
        hasHealthInsurance: normalizeBoolean(data.hasHealthInsurance),
        isPCD: normalizeBoolean(data.isPCD),
        hasCajuBenefit: normalizeBoolean(data.hasCajuBenefit),
        hasWellhubBenefit: normalizeBoolean(data.hasWellhubBenefit),
        hasZeloBenefit: normalizeBoolean(data.hasZeloBenefit),
        hasGuapecoBenefit: normalizeBoolean(data.hasGuapecoBenefit),
        showVacationPeriods: normalizeBoolean(data.showVacationPeriods),
        isRaroAcademyAlumnus: normalizeBoolean(data.isRaroAcademyAlumnus),
        hasOnboardingKit: normalizeBoolean(data.hasOnboardingKit),
        hasBackpack: normalizeBoolean(data.hasBackpack),
        hasNotice: normalizeBoolean(data.hasNotice),
        salaryHistoryShowHistory: normalizeBoolean(data.salaryHistoryShowHistory),
        salaryHistoryAllowRemove: normalizeBoolean(data.salaryHistoryAllowRemove),

        // Arrays que devem ser vazios no formulário
        addresses: normalizeArray(data.addresses),
        children: normalizeArray(data.children),
        healthDependents: normalizeArray(data.healthDependents),
        medicalCertificates: normalizeArray(data.medicalCertificates),
        zeloDependents: normalizeArray(data.zeloDependents),
        pcdTypes: normalizeArray(data.pcdTypes),
        vacationPeriods: normalizeArray(data.vacationPeriods),
        paidRestPeriods: normalizeArray(data.paidRestPeriods),
        occurrences: normalizeArray(data.occurrences),
        raroFeedbacks: normalizeArray(data.raroFeedbacks),
        clientFeedbacks: normalizeArray(data.clientFeedbacks),
        salaryHistorySalaries: normalizeArray(data.salaryHistorySalaries),
        resources: normalizeArray(data.resources),
        preferredWorkModels: normalizeArray(data.preferredWorkModels),

        // Objetos que podem ser undefined
        identityDocument: normalizeObject(data.identityDocument),
        emergencyContact: normalizeObject(data.emergencyContact),
        salaryAccount: normalizeObject(data.salaryAccount),
        personalAccount: normalizeObject(data.personalAccount),

        // Enums que devem ser undefined se null
        sex: data.sex ?? undefined,
        gender: normalizeString(data.gender),
        race: data.race ?? undefined,
        maritalStatus: data.maritalStatus ?? undefined,
        collaboratorType: data.collaboratorType ?? undefined,
        roleType: data.roleType ?? undefined,
        shirtSize: data.shirtSize ?? undefined,

        // Números que podem ser undefined
        rating: data.rating ?? undefined,
        transportationBenefitAmount: data.transportationBenefitAmount ?? undefined,
        workSchedule: data.workSchedule ?? undefined,
        newSalaryEntryAmount: data.newSalaryEntryAmount ?? undefined,
        newSalaryEntryExtras: data.newSalaryEntryExtras ?? undefined,
    };
});

// Helpers contextuais unificados para o schema do colaborador
const getMetadata = (fieldPath: string): FieldMetadata | null => {
    const field = BaseCollaboratorSchema.shape[fieldPath as keyof typeof BaseCollaboratorSchema.shape];
    if (field && field.description) {
        try {
            return JSON.parse(field.description);
        } catch {
            return null;
        }
    }
    return null;
};

/**
 * Mapeamento de steps do ProfileUpdateWizard para fieldsets
 */
export const PROFILE_UPDATE_STEP_FIELDSETS: Record<number, string[]> = {
    // -1 InstructionStep - sem validações
    '-1': [], // ✅ Explicitamente definido como array vazio
    0: ['PersonalDataFieldSet'], // PersonalDataStep
    1: ['AddressFieldSet'], // AddressStep
    2: ['IdentificationFieldSet', 'SignedContractsFieldSet'], // DocumentsStep
    3: ['BankAccountFieldSet'], // BankDetailsStep
    4: ['FiliationAndMaritalStatusFieldSet', 'ChildrenFieldSet'], // FamilyStep
    5: ['EmergencyContactFieldSet', 'HealthPlanFieldSet', 'MedicalExamsFieldSet', 'SpecialNeedsFieldSet', 'MedicalCertificatesFieldSet'], // HealthStep
    6: ['GiftsAndPreferencesFieldSet'], // AboutYouStep
};

/**
 * Cria um schema contextual que aplica validações baseadas no contexto e fieldsets atuais
 * @param context - Contexto do formulário (FULL_FORM, PROFILE_UPDATE, etc.)
 * @param currentFieldsets - Array de fieldsets que devem ser validados (opcional)
 * @param hasNoBmgAccount - Se true, torna campos de conta salário opcionais (opcional)
 * @returns Schema Zod com validações contextuais aplicadas
 */
export const createContextualCollaboratorSchema = (context: string, currentFieldsets?: string[], hasNoBmgAccount?: boolean) => {
    // Para contextos que precisam de validações especiais
    if (context === 'PROFILE_UPDATE' || context === 'DHO_UPDATE' || context === 'DP_UPDATE' || context === 'TECH_UPDATE') {
        // ✅ CORREÇÃO: Criar schema de endereço contextual para evitar validações desnecessárias
        const isAddressFieldsetActive = currentFieldsets?.includes('AddressFieldSet') ?? true;
        const contextualAddressSchema = isAddressFieldsetActive
            ? z.object({    // Schema contextual para AddressFieldSet ativo
                type: z.enum(['main', 'mailing']).optional(),
                street: z.string().optional(),
                number: z.string().optional(),
                complement: z.string().optional(),
                neighborhood: z.string().optional(),
                city: z.string().optional(),
                state: z.string().optional(),
                // ✅ Aceita qualquer valor - validação específica é feita no Controller
                proofOfAddressFile: z.any().optional(),
                proofOfAddressUrl: z.string().optional(),
                fullAddress: z.string().optional(),
                id: z.string().optional(),
                zipCode: z.string().optional(),
                country: z.string().optional(),
                latitude: z.number().optional(),
                longitude: z.number().optional(),
            })
            : z.object({    // Schema mínimo quando AddressFieldSet não está ativo
                type: z.enum(['main', 'mailing']).optional(),
                street: z.string().optional(),
                number: z.string().optional(),
                complement: z.string().optional(),
                neighborhood: z.string().optional(),
                city: z.string().optional(),
                state: z.string().optional(),
                proofOfAddressFile: z.any().optional(), // ✅ Aceita qualquer coisa sem validar File
                proofOfAddressUrl: z.string().optional(),
                fullAddress: z.string().optional(),
                id: z.string().optional(),
                zipCode: z.string().optional(),
                country: z.string().optional(),
                latitude: z.number().optional(),
                longitude: z.number().optional(),
            });

        // Criar um schema derivado do base mas com 'name' e 'updatedStatus' opcionais
        const contextualSchema = BaseCollaboratorSchema.extend({
            name: context === 'PROFILE_UPDATE' ? z.string().optional() : z.string(),
            updatedStatus: z.any().optional(), // updatedStatus nunca deve ser validado nos formulários contextuais
            // ✅ CORREÇÃO: Substituir addresses por versão contextual
            addresses: withMetadata(
                z.array(contextualAddressSchema).nullable().optional(),
                {
                    contexts: ['FULL_FORM', 'PROFILE_UPDATE'],
                    required: [],
                    fieldset: 'AddressFieldSet'
                }
            ),
        });

        // Adicionar validações contextuais para campos obrigatórios no contexto atual
        const contextualValidations: Record<string, any> = {};

        const nestedRequiredPaths: string[][] = [];
        Object.keys(BaseCollaboratorSchema.shape).forEach(fieldPath => {
            const metadata = getMetadata(fieldPath);
            
            // ✅ CORREÇÃO: Pular validação de salaryAccount quando usuário não tem conta BMG
            const isSalaryAccountField = fieldPath.startsWith('salaryAccount.');
            const shouldSkipSalaryAccountValidation = hasNoBmgAccount && isSalaryAccountField;
            
            if (metadata && metadata.contexts.includes(context) && metadata.required.includes(context) && !shouldSkipSalaryAccountValidation) {
                // Se currentFieldsets foi fornecido, só validar campos dos fieldsets especificados
                if (currentFieldsets !== undefined) {
                    if (currentFieldsets.length === 0) {
                        // Se array está vazio, não validar nenhum campo (InstructionStep)
                        return;
                    }
                    if (!currentFieldsets.includes(metadata.fieldset)) {
                        // Se fieldset específico não está na lista, pular validação
                        return;
                    }
                }

                if (fieldPath.includes('.')) {
                    // Store nested path for later superRefine validation
                    nestedRequiredPaths.push(fieldPath.split('.'));
                } else {
                    // existing logic for direct fields
                    const fieldSchema = BaseCollaboratorSchema.shape[fieldPath as keyof typeof BaseCollaboratorSchema.shape];
                    if (fieldSchema) {
                        let requiredSchema: any = fieldSchema;
                        if (requiredSchema instanceof z.ZodString) {
                            requiredSchema = z.string({ required_error: 'Campo obrigatório' }).nonempty('Campo obrigatório');
                        } else {
                            requiredSchema = requiredSchema.refine((val: any) => {
                                if (val === null || val === undefined) return false;
                                if (typeof val === 'string' && val.trim() === '') return false;  // ← CORREÇÃO!
                                if (Array.isArray(val)) return val.length > 0;
                                return true;
                            }, 'Campo obrigatório');
                        }
                        contextualValidations[fieldPath] = requiredSchema;
                    }
                }
            }
        });

        let schema: any = contextualSchema.extend(contextualValidations);

        if (nestedRequiredPaths.length > 0) {
            schema = schema.superRefine((obj: any, ctx: z.RefinementCtx) => {
                nestedRequiredPaths.forEach(pathArr => {
                    let current: any = obj;
                    for (const segment of pathArr) {
                        current = Array.isArray(current) ? current[Number(segment)] : current?.[segment];
                        if (current === undefined) break;
                    }
                    const value = current;
                    const isEmptyString = typeof value === 'string' && value.trim() === '';
                    if (value === undefined || value === null || isEmptyString) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: pathArr,
                            message: 'Campo obrigatório',
                        });
                    }
                });
            });
        }

        // ✅ TODAS as validações agora são feitas via sistema de metadados integrado
        // Validações contextuais que respeitam fieldsets ativos

        // Validação condicional para spouseName baseado em maritalStatus
        if (context === 'PROFILE_UPDATE' && currentFieldsets?.includes('FiliationAndMaritalStatusFieldSet')) {
            schema = schema.refine((data: any) => {
                const maritalStatus = data.maritalStatus;
                const spouseName = data.spouseName;

                // Se o estado civil exige cônjuge e não há nome do cônjuge preenchido
                const needsSpouse = maritalStatus === 'Casado(a)' || maritalStatus === 'União Estável';
                const hasSpouseName = spouseName && spouseName.trim() !== '';

                return !needsSpouse || hasSpouseName;
            }, {
                message: 'Nome do cônjuge é obrigatório para estado civil Casado(a) ou União Estável',
                path: ['spouseName']
            });
        }

        // ✅ REMOVIDO: Validação contextual para emergencyContact nested fields
        // Agora os campos individuais (emergencyContact.name, emergencyContact.phone, emergencyContact.relationship)
        // são validados automaticamente pelo sistema de metadados integrado

        // Validação contextual para identityDocument nested fields
        if (context === 'PROFILE_UPDATE' && currentFieldsets?.includes('IdentificationFieldSet')) {
            schema = schema.refine((data: any) => {
                const doc = data.identityDocument;
                if (!doc) return true; // Se null/undefined, a validação de obrigatoriedade é tratada pelo sistema geral

                // Se o documento existe, todos os campos obrigatórios devem estar preenchidos
                return doc.number && doc.issuer && doc.uf && doc.issueDate;
            }, {
                message: 'Documento de identidade deve ter número, órgão emissor, UF e data de emissão preenchidos',
                path: ['identityDocument']
            });
        }

        // Validação contextual para comprovante de endereço: arquivo OU URL obrigatório
        if (context === 'PROFILE_UPDATE' && currentFieldsets?.includes('AddressFieldSet')) {
            schema = schema.refine((data: any) => {
                const addresses = data.addresses;
                if (!addresses || !addresses[0]) return true; // Se não há endereço, não validar

                const address = addresses[0];
                const hasFile = address.proofOfAddressFile instanceof File;
                const hasUrl = address.proofOfAddressUrl && address.proofOfAddressUrl.trim() !== '';
                
                return hasFile || hasUrl;
            }, {
                message: 'Comprovante de endereço é obrigatório',
                path: ['addresses', 0, 'proofOfAddressFile']
            });
        }

        // Validação contextual para documento de identificação: arquivo OU URL obrigatório
        if (context === 'PROFILE_UPDATE' && currentFieldsets?.includes('IdentificationFieldSet')) {
            schema = schema.refine((data: any) => {
                const hasFile = data.idDocumentFrontFile instanceof File;
                const hasUrl = data.identityDocument?.idDocumentFrontUrl && 
                              data.identityDocument.idDocumentFrontUrl.trim() !== '';
                
                return hasFile || hasUrl;
            }, {
                message: 'Documento de identificação é obrigatório',
                path: ['idDocumentFrontFile']
            });
        }

        // Validações contextuais para contratos assinados: arquivo OU URL obrigatório
        if (context === 'DP_UPDATE' && currentFieldsets?.includes('SignedContractsFieldSet')) {
            // Contrato de Trabalho: arquivo OU URL obrigatório
            schema = schema.refine((data: any) => {
                const hasFile = data.workContractFile instanceof File;
                const hasUrl = data.workContractUrl && data.workContractUrl.trim() !== '';
                return hasFile || hasUrl;
            }, {
                message: 'Contrato de trabalho é obrigatório (arquivo ou URL)',
                path: ['workContractFile']
            });
        }

        // Validação contextual para salaryAccount nested fields
        if (context === 'PROFILE_UPDATE' && currentFieldsets?.includes('BankAccountFieldSet') && !hasNoBmgAccount) {
            schema = schema.refine((data: any) => {
                const account = data.salaryAccount;
                if (!account) return true; // Se null/undefined, a validação de obrigatoriedade é tratada pelo sistema geral

                // Se a conta existe, todos os campos obrigatórios devem estar preenchidos
                return account.bankCode && account.agency && account.account && account.digit;
            }, {
                message: 'Conta salário deve ter banco, agência, conta e dígito preenchidos',
                path: ['salaryAccount']
            });

            // Validação adicional para banco BMG
            schema = schema.refine((data: any) => {
                const account = data.salaryAccount;
                if (!account || !account.bankCode) return true;
                return account.bankCode === '318';
            }, {
                message: 'Banco da conta salário deve ser BMG (código 318)',
                path: ['salaryAccount', 'bankCode']
            });
        }

        return schema;
    }

    // Para outros contextos, usar o schema normal
    return CollaboratorSchema;
};

export const collaboratorSchemaHelpers = {
    getMetadata,

    shouldShowField: (fieldPath: string, context: string): boolean => {
        const metadata = collaboratorSchemaHelpers.getMetadata(fieldPath);
        return metadata ? metadata.contexts.includes(context) : false;
    },

    isFieldRequired: (fieldPath: string, context: string): boolean => {
        const metadata = collaboratorSchemaHelpers.getMetadata(fieldPath);
        return metadata ? metadata.required.includes(context) : false;
    },

    getFieldsByFieldset: (fieldsetName: string): string[] => {
        return Object.keys(BaseCollaboratorSchema.shape).filter(fieldPath => {
            const metadata = collaboratorSchemaHelpers.getMetadata(fieldPath);
            return metadata?.fieldset === fieldsetName;
        });
    },

    getRequiredFieldsByFieldsets: (fieldsetNames: string[], context: string): string[] => {
        return Object.keys(BaseCollaboratorSchema.shape).filter(fieldPath => {
            const metadata = collaboratorSchemaHelpers.getMetadata(fieldPath);
            return metadata &&
                fieldsetNames.includes(metadata.fieldset) &&
                metadata.contexts.includes(context) &&
                metadata.required.includes(context);
        });
    },

    getAllFieldsWithMetadata: (): Record<string, FieldMetadata> => {
        const fieldsWithMetadata: Record<string, FieldMetadata> = {};
        Object.keys(BaseCollaboratorSchema.shape).forEach(fieldPath => {
            const metadata = collaboratorSchemaHelpers.getMetadata(fieldPath);
            if (metadata) {
                fieldsWithMetadata[fieldPath] = metadata;
            }
        });
        return fieldsWithMetadata;
    },

    /**
     * Transforma dados do backend (com null/undefined) para dados adequados ao formulário
     * Resolve o problema de "controlled vs uncontrolled components"
     * 
     * @param backendData - Dados vindos do backend que podem ter null/undefined
     * @returns Dados normalizados para o formulário (strings vazias, arrays vazios, etc.)
     * 
     * @example
     * ```typescript
     * // ❌ Antes - dados do backend causavam warning
     * const backendData = { personalEmail: null, phone: undefined };
     * 
     * // ✅ Depois - dados normalizados para formulário
     * const formData = collaboratorSchemaHelpers.toFormData(backendData);
     * // Result: { personalEmail: '', phone: '', ... }
     * ```
     */
    toFormData: (backendData: Collaborator): FormCollaborator => {
        return FormCollaboratorSchema.parse(backendData);
    },

    /**
     * Transforma dados do formulário para o formato CollaboratorSelf (coleção collaborators_self)
     * Filtra apenas campos permitidos e remove undefined para Firebase
     * 
     * @param formData - Dados do formulário normalizados
     * @returns Dados no formato adequado para collaborators_self
     */
    toSelfData: (formData: any) => {
        // Helper para converter strings vazias de volta para null
        const denormalizeString = (value: string): string | null => value === '' ? null : value;

        // Helper para converter arrays vazios para null
        const denormalizeArray = <T>(value: T[]): T[] | null => value.length === 0 ? null : value;

        const backendData = {
            ...formData,
            // Reverter strings vazias para null
            personalEmail: denormalizeString(formData.personalEmail),
            phone: denormalizeString(formData.phone),
            birthDate: denormalizeString(formData.birthDate),
            nationality: denormalizeString(formData.nationality),
            naturality: denormalizeString(formData.naturality),
            birthState: denormalizeString(formData.birthState),
            cpf: denormalizeString(formData.cpf),
            // ... outros campos conforme necessário
        } as Collaborator;

        // 🔥 REMOVER undefined DE FORMA RECURSIVA (Firestore não aceita undefined)
        const stripUndefinedDeep = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(stripUndefinedDeep);
            }
            if (obj !== null && typeof obj === 'object') {
                const cleaned: any = {};
                Object.entries(obj).forEach(([key, value]) => {
                    const cleanedValue = stripUndefinedDeep(value);
                    if (cleanedValue !== undefined) {
                        cleaned[key] = cleanedValue;
                    }
                });
                return cleaned;
            }
            return obj;
        };

        return stripUndefinedDeep(backendData) as Collaborator;
    },

    /**
     * Transforma dados do formulário de volta para o formato do backend
     * Remove strings vazias e converte para null quando apropriado
     * 
     * @param formData - Dados do formulário normalizados
     * @returns Dados no formato adequado para o backend
     */
    toBackendData: (formData: FormCollaborator): Collaborator => {
        // Helper para converter strings vazias de volta para null
        const denormalizeString = (value: string): string | null => value === '' ? null : value;

        // Helper para converter arrays vazios para null
        const denormalizeArray = <T>(value: T[]): T[] | null => value.length === 0 ? null : value;

        // IMPORTANTE: Preservar campos de salário que são usados durante o save
        // mas não fazem parte do schema principal do Collaborator
        const salaryFields: any = {};
        const salaryFieldKeys = [
            'salaryHistoryDeletedIds',
            'salaryHistorySalaries',
            'newSalaryEntryAmount',
            'newSalaryEntryExtras',
            'newSalaryEntrySalaryType',
            'newSalaryEntryDate',
            'newSalaryEntryReason',
            'newSalaryEntryObservations',
            'salaryHistoryAllowRemove',
            'salaryHistoryShowHistory'
        ];

        // Extrair campos de salário antes de processar
        salaryFieldKeys.forEach(key => {
            if ((formData as any)[key] !== undefined) {
                salaryFields[key] = (formData as any)[key];
            }
        });

        const backendData = {
            ...formData,
            // Reverter strings vazias para null
            personalEmail: denormalizeString(formData.personalEmail),
            phone: denormalizeString(formData.phone),
            birthDate: denormalizeString(formData.birthDate),
            nationality: denormalizeString(formData.nationality),
            naturality: denormalizeString(formData.naturality),
            birthState: denormalizeString(formData.birthState),
            cpf: denormalizeString(formData.cpf),
            // Preservar campos de salário
            ...salaryFields,
            // ... outros campos conforme necessário
        } as Collaborator;

        if (Array.isArray((backendData as any).preferredWorkModels) && (backendData as any).preferredWorkModels.length === 0) {
            delete (backendData as any).preferredWorkModels;
        }

        // 🔥 REMOVER undefined DE FORMA RECURSIVA (Firestore não aceita undefined)
        const stripUndefinedDeep = (obj: any): any => {
            if (Array.isArray(obj)) {
                return obj.map(stripUndefinedDeep);
            }
            if (obj !== null && typeof obj === 'object') {
                const cleaned: any = {};
                Object.entries(obj).forEach(([key, value]) => {
                    const cleanedValue = stripUndefinedDeep(value);
                    if (cleanedValue !== undefined) {
                        cleaned[key] = cleanedValue;
                    }
                });
                return cleaned;
            }
            return obj;
        };

        return stripUndefinedDeep(backendData) as Collaborator;
    },

};

// Tipos derivados dos schemas
export type Child = z.infer<typeof ChildSchema>;
export type FeedbackEntry = z.infer<typeof FeedbackEntrySchema>;
export type HealthDependent = z.infer<typeof HealthDependentSchema>;
export type EmergencyContact = z.infer<typeof EmergencyContactSchema>;
export type VacationPeriod = z.infer<typeof VacationPeriodSchema>;
export type PaidRestPeriod = z.infer<typeof PaidRestPeriodSchema>;
export type MedicalCertificate = z.infer<typeof MedicalCertificateSchema>;
export type IdentityDocument = z.infer<typeof IdentityDocumentSchema>;
export type Resource = z.infer<typeof ResourceSchema>;
export type RoleType = z.infer<typeof RoleTypeSchema>;
export type ShirtSize = z.infer<typeof ShirtSizeSchema>;
export type CollaboratorType = z.infer<typeof CollaboratorTypeSchema>;
export type StatusType = z.infer<typeof StatusTypeSchema>;
export type UpdateStatusEntry = z.infer<typeof UpdateStatusEntrySchema>;
export type BaseCollaborator = z.infer<typeof BaseCollaboratorSchema>;
export type Collaborator = z.infer<typeof CollaboratorSchema>;
export type FormCollaborator = z.infer<typeof FormCollaboratorSchema>;
export type WorkModelPreference = z.infer<typeof WorkModelPreferenceSchema>;
export type PcdType = z.infer<typeof PcdTypeSchema>;
export type SalaryEntry = z.infer<typeof SalaryEntrySchema>;
export type EducationEntry = z.infer<typeof EducationEntrySchema>;

// Tipos de férias (reexportados de schemas)
export type { CollaboratorVacationSummary, NextVacationData } from './collaborator.types';
